import React, { useState, useEffect, useMemo } from "react"
import { Modal, Button, Flex, Select } from "antd"
import { Trans, useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import CustomPagination from "components/common/CustomPagination"
import styles from "../roleModals.module.css"

const DeleteRoleWithReassignModal = ({ open, onCancel, onConfirm, roleName, assignedStaff = [], availableRoles = [] }) => {
  const { t } = useTranslation()
  const [selectedNewRole, setSelectedNewRole] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 3

  const hasAssignedStaff = assignedStaff.length > 0
  const staffCount = assignedStaff.length
  const hasAvailableRoles = availableRoles.length > 0
  const showPagination = staffCount > pageSize

  const paginatedStaff = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return assignedStaff.slice(startIndex, endIndex)
  }, [assignedStaff, currentPage, pageSize])

  useEffect(() => {
    if (!open) {
      setSelectedNewRole(null)
      setCurrentPage(1)
    }
  }, [open])

  const handleConfirm = () => {
    onConfirm(hasAssignedStaff ? selectedNewRole : null)
  }

  const canDelete = !hasAssignedStaff || (hasAvailableRoles && selectedNewRole)

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={hasAssignedStaff ? 400 : 370}
      className={styles.roleModal}
      maskClosable={false}
      closeIcon={
        <span className={styles.closeIcon}>
          <Icon type="feather" name="X" />
        </span>
      }
    >
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>{t("staff.role_modal.delete_title")}</h2>
      </div>
      <div className={styles.modalBody}>
        {hasAssignedStaff ? (
          <>
            <Text className={styles.deleteDescription}>
              <Trans
                i18nKey={staffCount > 1 ? "staff.role_modal.delete_assigned_message_plural" : "staff.role_modal.delete_assigned_message"}
                values={{ roleName, count: staffCount }}
                components={{ strong: <strong /> }}
              />
              <br />
              {t("staff.role_modal.delete_select_new_role")}
            </Text>
            <div className={styles.staffListSection}>
              <Text className={styles.staffListTitle}>
                {roleName ? t("staff.role_modal.staff_list_title", { roleName }) : t("staff.role_modal.staff_list_title_default")}
              </Text>
              <div className={styles.staffList}>
                {paginatedStaff.map((staff, index) => {
                  const globalIndex = (currentPage - 1) * pageSize + index + 1
                  return (
                    <div key={staff.id} className={styles.staffListItem}>
                      <Text className={styles.staffItemName}>
                        {globalIndex}. {staff.name}
                      </Text>
                      {staff.phone_number && (
                        <Flex gap={8} align="center" className={styles.staffItemContact}>
                          <Icon type="feather" name="Phone" width={14} height={14} />
                          <Text className={styles.staffItemContactText}>{staff.phone_number}</Text>
                        </Flex>
                      )}
                      {staff.email && (
                        <Flex gap={8} align="center" className={styles.staffItemContact}>
                          <Icon type="feather" name="Mail" width={14} height={14} />
                          <Text className={styles.staffItemContactText}>{staff.email}</Text>
                        </Flex>
                      )}
                    </div>
                  )
                })}
              </div>
              {showPagination && (
                <Flex justify="center">
                  <CustomPagination
                    pageSize={pageSize}
                    total={staffCount}
                    onPageChange={setCurrentPage}
                    showPageSizeSelector={false}
                    metadata={{
                      page: currentPage,
                      total_pages: Math.ceil(staffCount / pageSize),
                      next_page: currentPage < Math.ceil(staffCount / pageSize) ? currentPage + 1 : null,
                      prev_page: currentPage > 1 ? currentPage - 1 : null
                    }}
                  />
                </Flex>
              )}
            </div>
            <div className={styles.roleReassignSection}>
              {!hasAvailableRoles && <Text className={styles.warningText}>{t("staff.role_modal.no_roles_available")}</Text>}
              <Flex gap={16} align="center">
                <div className={styles.currentRoleBox}>
                  <Text className={styles.roleLabel}>{t("staff.role_modal.current_role")}</Text>
                  <div className={styles.roleValue}>{roleName}</div>
                </div>
                <Icon type="feather" name="ArrowRight" width={20} height={20} className={styles.arrowIcon} />
                <div className={styles.newRoleBox}>
                  <Text className={styles.roleLabel}>{t("staff.role_modal.new_role")}</Text>
                  <Select
                    placeholder={hasAvailableRoles ? t("staff.role_modal.select_a_role") : t("staff.role_modal.no_roles")}
                    style={{ width: "100%" }}
                    value={selectedNewRole}
                    onChange={setSelectedNewRole}
                    disabled={!hasAvailableRoles}
                    options={availableRoles.map((role) => ({
                      label: role.name,
                      value: role.id
                    }))}
                  />
                </div>
              </Flex>
            </div>
          </>
        ) : (
          <Text className={styles.deleteDescription}>
            <Trans i18nKey="staff.role_modal.delete_permanent_message" values={{ roleName }} components={{ strong: <strong /> }} />
            <br />
            {t("staff.role_modal.delete_cant_undo")}
          </Text>
        )}
      </div>
      <Flex gap={12} className={styles.modalFooter}>
        <Button size="large" onClick={onCancel} variant="outlined" color="primary">
          {t("staff.role_modal.cancel")}
        </Button>
        <Button type="primary" danger size="large" onClick={handleConfirm} disabled={!canDelete}>
          {t("staff.role_modal.delete")}
        </Button>
      </Flex>
    </Modal>
  )
}

export default DeleteRoleWithReassignModal
